// © 2026 AttainIP Engineering
/**
 * Cookie Consent Banner — aligned with marketing site (attain-ss-public)
 *
 * Uses the same localStorage keys (`cookie_consent`, `cookie_preferences`)
 * and 5-category model so consent carries across the shared origin.
 * Geo-detection auto-accepts for non-GDPR countries.
 */

import { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { Switch, Button } from '@attain/ui-lib/components/base';
import { identifyUser } from '../lib/mixpanel';
import { initConsentAwareTags } from '../lib/tagLoader';
import { shouldShowConsentBanner } from '../lib/geo';
import type { CookiePreferences } from '../lib/consent';
import { useAuthStore } from '../store';

// ─── Storage keys (shared with marketing site) ──────────────────────────────

const COOKIE_CONSENT_KEY = 'cookie_consent';
const COOKIE_PREFERENCES_KEY = 'cookie_preferences';
const OLD_CONSENT_KEY = 'attain_cookie_consent';

type ConsentType = 'accepted' | 'rejected' | 'customised';

const defaultPreferences: CookiePreferences = {
  necessary: true,
  functional: false,
  analytics: false,
  performance: false,
  advertisement: false,
};

const cookieCategories = [
  {
    id: 'necessary' as const,
    name: 'Necessary',
    alwaysActive: true,
    description:
      'Necessary cookies are required to enable the basic features of this site, such as providing secure log-in or adjusting your consent preferences. These cookies do not store any personally identifiable data.',
  },
  {
    id: 'functional' as const,
    name: 'Functional',
    alwaysActive: false,
    description:
      'Functional cookies help perform certain functionalities like sharing the content of the website on social media platforms, collecting feedback, and other third-party features.',
  },
  {
    id: 'analytics' as const,
    name: 'Analytics',
    alwaysActive: false,
    description:
      'Analytical cookies are used to understand how visitors interact with the website. These cookies help provide information on metrics such as the number of visitors, bounce rate, traffic source, etc.',
  },
  {
    id: 'performance' as const,
    name: 'Performance',
    alwaysActive: false,
    description:
      'Performance cookies are used to understand and analyze the key performance indexes of the website which helps in delivering a better user experience for the visitors.',
  },
  {
    id: 'advertisement' as const,
    name: 'Advertisement',
    alwaysActive: false,
    description:
      'Advertisement cookies are used to provide visitors with customised advertisements based on the pages you visited previously and to analyze the effectiveness of the ad campaigns.',
  },
];

// ─── Migration helper ────────────────────────────────────────────────────────

/**
 * Migrate from old selfservice consent key to the shared marketing-site keys.
 * Only runs once — when old key exists but new key doesn't.
 */
function migrateOldConsent(): void {
  try {
    const oldValue = localStorage.getItem(OLD_CONSENT_KEY);
    if (!oldValue || localStorage.getItem(COOKIE_CONSENT_KEY)) return;

    const old = JSON.parse(oldValue) as { analytics?: boolean };
    const prefs: CookiePreferences = {
      necessary: true,
      functional: false,
      analytics: !!old.analytics,
      performance: false,
      advertisement: false,
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, old.analytics ? 'accepted' : 'rejected');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    localStorage.removeItem(OLD_CONSENT_KEY);
  } catch {
    // ignore
  }
}

// ─── Tag activation ──────────────────────────────────────────────────────────

function activateTagsIfConsented(): void {
  initConsentAwareTags();
  const user = useAuthStore.getState().user;
  if (user) identifyUser(user);
}

// ─── Component ───────────────────────────────────────────────────────────────

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showCustomise, setShowCustomise] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);

  useEffect(() => {
    // Migrate old key first
    migrateOldConsent();

    const checkConsent = async () => {
      const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (consent) {
        // Already decided — activate tags if appropriate
        activateTagsIfConsented();
        return;
      }

      // Geo-detect: should we show the banner?
      const showBanner = await shouldShowConsentBanner();

      if (!showBanner) {
        // Non-GDPR country: auto-accept all tracking
        const allAccepted: CookiePreferences = {
          necessary: true,
          functional: true,
          analytics: true,
          performance: true,
          advertisement: true,
        };
        localStorage.setItem(COOKIE_CONSENT_KEY, 'auto_accepted');
        localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(allAccepted));
        activateTagsIfConsented();
      } else {
        // EU/UK user: show banner after short delay
        const timer = setTimeout(() => setVisible(true), 500);
        return () => clearTimeout(timer);
      }
    };

    checkConsent();
  }, []);

  // Cross-tab sync: listen for storage changes to cookie_preferences
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === COOKIE_PREFERENCES_KEY) {
        // Another tab changed consent — re-evaluate analytics
        activateTagsIfConsented();
        // If banner is showing and consent was given in another tab, hide it
        if (localStorage.getItem(COOKIE_CONSENT_KEY)) {
          setVisible(false);
          setShowCustomise(false);
        }
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const handleConsent = useCallback((type: ConsentType) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, type);

    if (type === 'accepted') {
      const allAccepted: CookiePreferences = {
        necessary: true,
        functional: true,
        analytics: true,
        performance: true,
        advertisement: true,
      };
      localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(allAccepted));
    } else if (type === 'rejected') {
      localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(defaultPreferences));
    }

    setVisible(false);
    setShowCustomise(false);
    setTimeout(() => activateTagsIfConsented(), 0);
  }, []);

  const handleSavePreferences = useCallback(() => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'customised');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(preferences));
    setVisible(false);
    setShowCustomise(false);
    setTimeout(() => activateTagsIfConsented(), 0);
  }, [preferences]);

  const togglePreference = useCallback((key: keyof CookiePreferences) => {
    if (key === 'necessary') return;
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  if (!visible) return null;

  // ── Customise Modal ──────────────────────────────────────────────────────

  if (showCustomise) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="bg-white rounded-xl shadow-lg max-w-lg w-full max-h-[90vh] overflow-hidden animate-in fade-in">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-200">
            <h3 className="text-gray-900 font-semibold text-lg">Customise Consent Preferences</h3>
            <button
              onClick={() => setShowCustomise(false)}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-5 overflow-y-auto max-h-[60vh]">
            <p className="text-gray-600 text-sm mb-6 leading-relaxed">
              We use cookies to help you navigate efficiently and perform certain functions. You will
              find detailed information about all cookies under each consent category below.
            </p>

            <div className="space-y-4">
              {cookieCategories.map((category) => (
                <div key={category.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-gray-900 font-medium">{category.name}</h4>
                    {category.alwaysActive ? (
                      <span className="text-sm text-green-600 font-medium">Always Active</span>
                    ) : (
                      <Switch
                        checked={preferences[category.id]}
                        onCheckedChange={() => togglePreference(category.id)}
                      />
                    )}
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed">{category.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex flex-wrap gap-ds-3 p-5 border-t border-gray-200 bg-gray-50">
            <Button variant="secondary" onClick={() => handleConsent('rejected')}>
              Reject All
            </Button>
            <Button variant="primary" onClick={handleSavePreferences}>
              Save Preferences
            </Button>
            <Button variant="primary" onClick={() => handleConsent('accepted')}>
              Accept All
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Initial Popup ────────────────────────────────────────────────────────

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm animate-in fade-in">
      <div className="bg-white rounded-xl p-5 shadow-lg border border-gray-200">
        <h3 className="text-gray-900 font-semibold text-lg mb-2">We value your privacy</h3>
        <p className="text-gray-600 text-sm mb-4 leading-relaxed">
          We use cookies to enhance your browsing experience, serve personalised content, and
          analyze our traffic. By clicking &quot;Accept All&quot;, you consent to our use of
          cookies.
        </p>
        <div className="flex flex-wrap gap-ds-3">
          <Button variant="secondary" onClick={() => setShowCustomise(true)}>
            Customise
          </Button>
          <Button variant="secondary" onClick={() => handleConsent('rejected')}>
            Reject All
          </Button>
          <Button variant="primary" onClick={() => handleConsent('accepted')}>
            Accept All
          </Button>
        </div>
      </div>
    </div>
  );
}
